import React, { useState, useEffect, useCallback } from 'react';
import { supabaseAdmin } from '../lib/supabase';
import styles from './TablePage.module.css';

const CONCEPTS = ['all', 'concept1', 'concept2', 'concept3', 'concept4', 'concept5'];

function chipStyle(active: boolean): React.CSSProperties {
  return {
    padding: '5px 14px', borderRadius: 999, border: 'none',
    background: active ? 'rgba(255,255,255,0.22)' : 'transparent',
    color: active ? 'white' : 'rgba(255,255,255,0.5)',
    fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
  };
}

const tdStyle: React.CSSProperties = { padding: '10px 14px', color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap' };

// ─── Tab 1: daily_stats ───────────────────────────────────────────
function DailyStatsTab() {
  const today = new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(today);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [emailMap, setEmailMap] = useState<Record<string, string>>({});
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [userRows, setUserRows] = useState<any[]>([]);
  const [userLoading, setUserLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    const { data } = await supabaseAdmin
      .from('daily_stats')
      .select('*')
      .eq('date', selectedDate)
      .order('total_count', { ascending: false });

    const list = data ?? [];
    setRows(list);

    const ids = Array.from(new Set(list.map((r: any) => r.user_id).filter(Boolean)));
    if (ids.length > 0) {
      const { data: users } = await supabaseAdmin.from('users').select('id, email').in('id', ids as string[]);
      const map: Record<string, string> = {};
      (users ?? []).forEach((u: any) => { map[u.id] = u.email; });
      setEmailMap(map);
    }
    setLoading(false);
  }, [selectedDate]);

  useEffect(() => { fetchStats(); setExpandedUser(null); }, [fetchStats]);

  async function toggleUser(userId: string) {
    if (expandedUser === userId) { setExpandedUser(null); return; }
    setExpandedUser(userId);
    setUserLoading(true);
    const { data } = await supabaseAdmin
      .from('daily_stats')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false });
    setUserRows(data ?? []);
    setUserLoading(false);
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <input
          type="date"
          className="input"
          value={selectedDate}
          onChange={e => setSelectedDate(e.target.value)}
          style={{ width: 160, padding: '6px 12px', fontSize: 13 }}
        />
        <button className="btn btn-ghost" onClick={fetchStats}>↺</button>
        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginLeft: 4 }}>
          {rows.length}명 학습
        </span>
      </div>

      {loading ? (
        <div className={styles.loading}>로딩 중...</div>
      ) : rows.length === 0 ? (
        <div className={styles.empty}><div className={styles.emptyIcon}>📭</div><div>해당 날짜 기록 없음</div></div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                {['유저', '정답/전체', '정확도', 'C1', 'C2', 'C3', 'C4', 'C5', ''].map(h => (
                  <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.45)', letterSpacing: 1 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const acc = r.total_count > 0 ? Math.round((r.correct_count / r.total_count) * 100) : 0;
                const isExpanded = expandedUser === r.user_id;
                return (
                  <React.Fragment key={r.id}>
                    <tr
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', background: isExpanded ? 'rgba(167,139,250,0.08)' : 'transparent' }}
                      onClick={() => toggleUser(r.user_id)}
                    >
                      <td style={{ ...tdStyle, color: 'rgba(255,255,255,0.7)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {emailMap[r.user_id] ?? r.user_id?.slice(0, 8) + '...'}
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>
                        <span style={{ color: '#34C98A' }}>{r.correct_count ?? 0}</span>
                        <span style={{ color: 'rgba(255,255,255,0.3)' }}>/</span>
                        <span>{r.total_count ?? 0}</span>
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 700, color: acc >= 70 ? '#34C98A' : acc >= 40 ? '#FFB84D' : '#F07880' }}>{acc}%</td>
                      {['concept1_count', 'concept2_count', 'concept3_count', 'concept4_count', 'concept5_count'].map(col => (
                        <td key={col} style={{ ...tdStyle, color: (r[col] ?? 0) > 0 ? '#C4A8FF' : 'rgba(255,255,255,0.2)' }}>
                          {r[col] ?? 0}
                        </td>
                      ))}
                      <td style={{ ...tdStyle, fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
                        {isExpanded ? '▲' : '▼'}
                      </td>
                    </tr>

                    {/* Expanded: all dates for this user */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={9} style={{ padding: '0 0 8px 0', background: 'rgba(167,139,250,0.04)' }}>
                          {userLoading ? (
                            <div style={{ padding: '16px 24px', color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>로딩 중...</div>
                          ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                                  {['날짜', '정답/전체', '정확도', 'C1', 'C2', 'C3', 'C4', 'C5'].map(h => (
                                    <th key={h} style={{ padding: '6px 14px', textAlign: 'left', fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.3)', letterSpacing: 1 }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {userRows.map(ur => {
                                  const uAcc = ur.total_count > 0 ? Math.round((ur.correct_count / ur.total_count) * 100) : 0;
                                  return (
                                    <tr key={ur.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: ur.date === selectedDate ? 'rgba(167,139,250,0.1)' : 'transparent' }}>
                                      <td style={{ ...tdStyle, fontSize: 12, fontWeight: ur.date === selectedDate ? 800 : 400, color: ur.date === selectedDate ? '#C4A8FF' : 'rgba(255,255,255,0.6)' }}>{ur.date}</td>
                                      <td style={{ ...tdStyle, fontSize: 12 }}>
                                        <span style={{ color: '#34C98A' }}>{ur.correct_count ?? 0}</span>
                                        <span style={{ color: 'rgba(255,255,255,0.3)' }}>/</span>
                                        <span>{ur.total_count ?? 0}</span>
                                      </td>
                                      <td style={{ ...tdStyle, fontSize: 12, fontWeight: 700, color: uAcc >= 70 ? '#34C98A' : uAcc >= 40 ? '#FFB84D' : '#F07880' }}>{uAcc}%</td>
                                      {['concept1_count', 'concept2_count', 'concept3_count', 'concept4_count', 'concept5_count'].map(col => (
                                        <td key={col} style={{ ...tdStyle, fontSize: 12, color: (ur[col] ?? 0) > 0 ? '#C4A8FF' : 'rgba(255,255,255,0.2)' }}>
                                          {ur[col] ?? 0}
                                        </td>
                                      ))}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── Tab 2: study_records ─────────────────────────────────────────
function StudyRecordsTab() {
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [concept, setConcept] = useState('all');
  const [emailMap, setEmailMap] = useState<Record<string, string>>({});

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    let query = supabaseAdmin
      .from('study_records')
      .select('*')
      .order('studied_at', { ascending: false })
      .limit(200);

    if (concept !== 'all') query = query.eq('content_type', concept);

    const { data } = await query;
    const rows = data ?? [];
    setRecords(rows);

    const ids = Array.from(new Set(rows.map((r: any) => r.user_id).filter(Boolean)));
    if (ids.length > 0) {
      const { data: users } = await supabaseAdmin.from('users').select('id, email').in('id', ids as string[]);
      const map: Record<string, string> = {};
      (users ?? []).forEach((u: any) => { map[u.id] = u.email; });
      setEmailMap(map);
    }
    setLoading(false);
  }, [concept]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const RESULT_COLOR: Record<string, string> = {
    correct: 'rgba(100,200,150,0.25)',
    synonym: 'rgba(255,184,77,0.2)',
    wrong: 'rgba(232,100,100,0.2)',
  };
  const RESULT_BORDER: Record<string, string> = {
    correct: 'rgba(100,200,150,0.5)',
    synonym: 'rgba(255,184,77,0.5)',
    wrong: 'rgba(232,100,100,0.4)',
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <div style={{ display: 'flex', background: 'rgba(255,255,255,0.1)', borderRadius: 999, padding: 3, gap: 2 }}>
          {CONCEPTS.map(c => (
            <button key={c} onClick={() => setConcept(c)} style={chipStyle(concept === c)}>
              {c === 'all' ? '전체' : c}
            </button>
          ))}
        </div>
        <button className="btn btn-ghost" onClick={fetchRecords} style={{ marginLeft: 'auto' }}>↺</button>
      </div>

      {loading ? (
        <div className={styles.loading}>로딩 중...</div>
      ) : records.length === 0 ? (
        <div className={styles.empty}><div className={styles.emptyIcon}>📭</div><div>기록 없음</div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {records.map((r) => (
            <div key={r.id} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              borderRadius: 10, padding: '10px 16px',
              border: `1px solid ${RESULT_BORDER[r.result ?? ''] ?? 'rgba(255,255,255,0.1)'}`,
              backgroundColor: RESULT_COLOR[r.result ?? ''] ?? 'rgba(255,255,255,0.05)',
            }}>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, padding: '3px 9px', borderRadius: 999, background: 'rgba(155,127,212,0.25)', color: '#C4A8FF', flexShrink: 0 }}>{r.content_type}</span>
              <span style={{ fontSize: 11, fontWeight: 700, flexShrink: 0, width: 55, color: r.result === 'correct' ? '#34C98A' : r.result === 'synonym' ? '#FFB84D' : '#F07880' }}>{r.result ?? '—'}</span>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', flexShrink: 0, width: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {emailMap[r.user_id] ?? r.user_id?.slice(0, 8) + '...'}
              </span>
              <span style={{ flex: 1, fontSize: 13, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.user_input ?? '—'}
              </span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', flexShrink: 0 }}>
                {new Date(r.studied_at).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────
export default function StudyRecordsPage() {
  const [tab, setTab] = useState<'daily' | 'records'>('daily');

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>📈 학습 기록</h1>
          <div className={styles.dbBadge}>🗄 study_records · daily_stats</div>
        </div>
      </div>

      <div style={{ display: 'flex', background: 'rgba(255,255,255,0.1)', borderRadius: 999, padding: 3, gap: 2, width: 'fit-content', marginBottom: 20 }}>
        <button onClick={() => setTab('daily')} style={chipStyle(tab === 'daily')}>일별 통계</button>
        <button onClick={() => setTab('records')} style={chipStyle(tab === 'records')}>상세 기록</button>
      </div>

      {tab === 'daily' ? <DailyStatsTab /> : <StudyRecordsTab />}
    </div>
  );
}
